import { useEffect, useState } from "react";
import { PageSEO } from "@/components/seo/PageSEO";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { FileDown, ExternalLink, Shield, Clock, CreditCard, Scale, AlertTriangle, Handshake } from "lucide-react";
import { motion } from "framer-motion";

interface DocItem {
  id: string;
  title: string;
  description: string | null;
  file_url: string;
  file_name: string;
  category: string;
}

function DocumentLink({ doc, openLabel }: { doc: DocItem; openLabel: string }) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    window.open(doc.file_url, "_blank", "noopener,noreferrer");
  };

  return (
    <a
      href={doc.file_url}
      onClick={handleClick}
      className="flex items-center gap-4 bg-card border rounded-xl p-5 hover:shadow-lg transition-all group cursor-pointer"
    >
      <div className="p-3 rounded-xl bg-coral/10 shrink-0">
        <FileDown className="h-6 w-6 text-coral" />
      </div>
      <div className="min-w-0 flex-1">
        <h4 className="font-semibold text-foreground text-base">{doc.title}</h4>
        {doc.description && (
          <p className="text-sm text-muted-foreground mt-0.5">{doc.description}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">{doc.file_name}</p>
      </div>
      <div className="flex items-center gap-2 text-sm font-medium text-coral shrink-0 group-hover:gap-3 transition-all">
        {openLabel} <ExternalLink className="h-4 w-4" />
      </div>
    </a>
  );
}

const summaryItems = [
  { key: "contract", icon: Handshake },
  { key: "payment", icon: CreditCard },
  { key: "data", icon: Shield },
  { key: "responsibility", icon: Scale },
  { key: "availability", icon: Clock },
  { key: "usage", icon: AlertTriangle },
];

export default function Villkor() {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const [docs, setDocs] = useState<DocItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    supabase
      .from("documents")
      .select("id, title, description, file_url, file_name, category")
      .eq("published", true)
      .order("sort_order")
      .then(({ data }) => {
        if (data) setDocs(data as unknown as DocItem[]);
        setLoaded(true);
      });
  }, []);

  return (
    <div>
      <PageSEO title={t("information.documents.title")} description={t("seo.villkor")} />

      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        <div className="absolute top-10 right-10 w-64 h-64 rounded-full border border-primary-foreground/5" />
        <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full border border-primary-foreground/5 translate-y-1/2" />

        <div className="relative container mx-auto px-4 lg:px-8 text-center max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-4">
              {t("villkor.hero.tagline")}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
              {t("villkor.hero.title")}
            </h1>
            <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed">
              {t("villkor.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms Summary */}
      <section className="py-20 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("villkor.summary.title")}
            </h2>
            <p className="mt-4 text-muted-foreground font-body text-lg">
              {t("villkor.summary.subtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {summaryItems.map((item, index) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-card rounded-2xl border border-border p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <item.icon className="h-6 w-6 text-primary dark:text-white" />
                </div>
                <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                  {t(`villkor.summary.items.${item.key}.title`)}
                </h3>
                <p className="text-sm text-muted-foreground font-body leading-relaxed">
                  {t(`villkor.summary.items.${item.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-center text-sm text-muted-foreground mt-10 max-w-xl mx-auto"
          >
            {t("villkor.summary.disclaimer")}
          </motion.p>
        </div>
      </section>

      {/* Download Documents */}
      <section className="py-20 lg:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t("villkor.download.title")}
            </h2>
            <p className="mt-4 text-muted-foreground font-body text-lg">
              {t("villkor.download.subtitle")}
            </p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {!loaded ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : docs.length === 0 ? (
              <p className="text-muted-foreground text-center">{t("villkor.download.empty")}</p>
            ) : (
              <div className="space-y-4">
                {docs.map((doc, index) => (
                  <motion.div
                    key={doc.id}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <DocumentLink doc={doc} openLabel={t("villkor.download.open")} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 lg:py-28 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading tracking-tight">
              {t("villkor.cta.title")}
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/70 font-body">
              {t("villkor.cta.subtitle")}
            </p>
            <div className="mt-8">
              <Link
                to={getLocalizedPath("/kontakt")}
                className="inline-flex items-center gap-2 bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8 py-3 rounded-md transition-colors"
              >
                {t("villkor.cta.button")}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
